import classNames from "classnames";
import React, { useContext } from "react";
import { RicosNodeContextProvider, RicosViewerContext } from "./../ricos-context/index.js";
import { Node_Type } from "./../ricos-schema/legacy/wix/rich_content/v1/index.js";
import { resolveBackwardCompatOverlay } from "./../_shared/resolve-backward-compat-overlay.js";
import { getImageSrc } from "./../wix-rich-content-common/lib/image-utils.js";
import { gradientToCss } from "../_shared/gradient-to-css.js";
import { hexToRgba } from "../theme-utils/color-utils.js";
import viewerStyles from "./card-viewer-scss.js";
import editorStyles from "./card-scss.js";
import {
  getCardBackgroundColorCssVar,
  getPageSizeByFormat,
  isTransparentCardBackgroundColor
} from "./shared-with-viewer.js";
const isHeaderLayout = (node) => !!node && node.type === Node_Type.LAYOUT && node.layoutData?.banner?.position === "TOP";
const isFooterLayout = (node) => !!node && node.type === Node_Type.LAYOUT && node.layoutData?.banner?.position === "BOTTOM";
const splitCardChildren = (nodes) => {
  const header = nodes.find(isHeaderLayout);
  const footer = nodes.find(isFooterLayout);
  const content = nodes.filter((n) => n !== header && n !== footer);
  return { header, content, footer };
};
const getBackgroundImageUrl = (backgroundImage) => {
  return backgroundImage.media?.src.url ?? (backgroundImage.media?.src.id ? getImageSrc({ file_name: backgroundImage.media.src.id }, void 0, {
    requiredQuality: 100,
    requiredWidth: Math.min(backgroundImage.media?.width ?? 4e3, 4e3),
    requiredHeight: Math.min(backgroundImage.media?.height ?? 4e3, 4e3)
  }) : void 0);
};
const getCardBackgroundStyles = (background, backgroundImage) => {
  const vars = {};
  const isTransparentColor = background?.type === "COLOR" && isTransparentCardBackgroundColor(background.color);
  const hasExplicitBackground = !!background && !isTransparentColor || !!backgroundImage;
  if (backgroundImage) {
    const imageUrl = getBackgroundImageUrl(backgroundImage);
    if (imageUrl) {
      vars["--ricos-internal-card-background-image"] = `url(${imageUrl})`;
    }
    if (backgroundImage.scaling) {
      vars["--ricos-internal-card-background-size"] = backgroundImage.scaling.toLowerCase();
    }
    if (backgroundImage.position) {
      vars["--ricos-internal-card-background-position"] = backgroundImage.position.toLowerCase().split("_").join(" ");
    }
    if (backgroundImage.overlay?.type === "GRADIENT") {
      const css = gradientToCss(backgroundImage.overlay.gradient);
      if (css) {
        vars["--ricos-internal-card-background-image-overlay"] = css;
      }
    } else if (backgroundImage.overlay?.type === "COLOR") {
      vars["--ricos-internal-card-background-image-overlay"] = hexToRgba(
        backgroundImage.overlay.color
      );
    } else if (background?.type === "GRADIENT") {
      const css = gradientToCss(background.gradient);
      if (css) {
        vars["--ricos-internal-card-background-image-overlay"] = css;
      }
    } else if (background?.type === "COLOR" && !isTransparentColor) {
      const resolved = resolveBackwardCompatOverlay(
        background.color,
        backgroundImage.opacity ?? 100,
        100
      );
      vars["--ricos-internal-card-background-image-overlay"] = hexToRgba(resolved);
    }
  } else {
    const backgroundColorCssVar = getCardBackgroundColorCssVar(background, hasExplicitBackground);
    if (backgroundColorCssVar) {
      vars["--ricos-internal-card-background-color"] = backgroundColorCssVar;
    }
    if (background?.type === "GRADIENT") {
      const css = gradientToCss(background.gradient);
      if (css) {
        vars["--ricos-internal-card-background-gradient"] = css;
      }
    }
    if (hasExplicitBackground) {
      vars["--ricos-internal-card-background-image"] = "none";
    }
  }
  return vars;
};
const CardContent = ({
  node,
  nodeIndex,
  RicosNodesRenderer
}) => {
  const { header, content, footer } = splitCardChildren(node.nodes ?? []);
  return /* @__PURE__ */ React.createElement(RicosNodeContextProvider, { parentNode: node, parentIndex: nodeIndex }, header && /* @__PURE__ */ React.createElement("div", { "data-card-header": true }, /* @__PURE__ */ React.createElement(RicosNodesRenderer, { nodes: [header], isRoot: true })), /* @__PURE__ */ React.createElement("div", { "data-card-content": true }, /* @__PURE__ */ React.createElement(RicosNodesRenderer, { nodes: content, isRoot: true })), footer && /* @__PURE__ */ React.createElement("div", { "data-card-footer": true }, /* @__PURE__ */ React.createElement(RicosNodesRenderer, { nodes: [footer], isRoot: true })));
};
const Card = ({
  node,
  nodeIndex,
  RicosNodesRenderer,
  pageFormat,
  mode,
  renderCardHeader,
  cardIndexOffset = 0
}) => {
  const pageSize = getPageSizeByFormat(pageFormat);
  const isFluid = pageSize.height === void 0;
  const cardData = node.cardData;
  const { isPrint } = useContext(RicosViewerContext) ?? {};
  const style = {
    "--ricos-internal-card-width": `${pageSize.width}px`,
    ...pageSize.height !== void 0 ? { "--ricos-internal-card-height": `${pageSize.height}px` } : {},
    ...getCardBackgroundStyles(
      cardData?.background,
      cardData?.backgroundImage
    )
  };
  const header = renderCardHeader?.({
    cardNode: node,
    index: nodeIndex + 1 + cardIndexOffset
  });
  if (mode === "editor") {
    return /* @__PURE__ */ React.createElement("div", { className: editorStyles.cardWrapper, "data-hook": "card-viewer-wrapper" }, header && /* @__PURE__ */ React.createElement("div", { className: editorStyles.cardHeaderContainer, "data-hook": "card-viewer-header" }, header), /* @__PURE__ */ React.createElement(
      "div",
      {
        className: classNames(editorStyles.cardContainer, {
          [editorStyles.fluid]: isFluid
        }),
        "data-hook": "card-viewer-container",
        style
      },
      /* @__PURE__ */ React.createElement(
        "div",
        {
          className: classNames(editorStyles.cardContent, {
            [editorStyles.fluidContent]: isFluid
          })
        },
        /* @__PURE__ */ React.createElement(
          CardContent,
          {
            node,
            nodeIndex,
            RicosNodesRenderer
          }
        )
      )
    ), /* @__PURE__ */ React.createElement("div", { className: editorStyles.cardGap, "data-hook": "card-viewer-gap" }));
  }
  const wrapperClassName = classNames(viewerStyles.cardWrapper, {
    [viewerStyles.fluid]: isFluid,
    [viewerStyles.print]: !!isPrint
  });
  return /* @__PURE__ */ React.createElement("div", { className: wrapperClassName, "data-hook": "card-viewer-wrapper" }, /* @__PURE__ */ React.createElement("div", { className: viewerStyles.cardContainer, "data-hook": "card-viewer-container", style }, /* @__PURE__ */ React.createElement("div", { className: viewerStyles.cardContent }, /* @__PURE__ */ React.createElement(CardContent, { node, nodeIndex, RicosNodesRenderer }))), /* @__PURE__ */ React.createElement("div", { className: viewerStyles.pageSeparator, "data-hook": "card-viewer-separator" }));
};
const createCardRenderer = ({
  pageFormat,
  mode,
  renderCardHeader,
  cardIndexOffset
}) => {
  return ({ node, ...props }) => /* @__PURE__ */ React.createElement(
    Card,
    {
      key: node.id,
      node,
      pageFormat,
      mode,
      renderCardHeader,
      cardIndexOffset,
      ...props
    }
  );
};
export {
  createCardRenderer
};
//# sourceMappingURL=card-viewer.js.map
